import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@material-ui/core";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import {
  CodeResponse,
  CredentialResponse,
  GoogleLogin,
  useGoogleLogin as useGoogle,
} from "@react-oauth/google";
import { getMsalInstance, ENTRA_LOGIN_SCOPES } from "@config/entraConfig";
import { getSession, setUser, State } from "@store";
import { GEARS_CONSTANTS, parseJwt } from "@utils";
import { GEARS_TOKEN, GEARS_TOKEN_EXPIRATION_DATE, ID_TOKEN } from "@constants";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { apiGeeAuthentication } from "@api/apiGeeAuthentication";
import { httpClient } from "@api/httpClient";
import { alertService } from "@services";

// Brand marks matching the login option icons used on tmtxcenter.globalpay.com
const GlobalPaymentsIcon: React.FC = () => (
  <svg height="24" width="24" viewBox="-.226 .99786345 18.452 16.00255017">
    <path
      d="m3.802 14.032c.388.242 1.033.511 1.715.511.621 0 1.198-.18 1.676-.487l.002-.001 1.805-1.128v4.073a1.56 1.56 0 0 1 -.824-.234z"
      fill="#225086"
    />
    <path
      d="m7.853 1.507-7.5 8.46c-.579.654-.428 1.642.323 2.111l3.126 1.954c.388.242 1.033.511 1.715.511.621 0 1.198-.18 1.676-.487l.002-.001 1.805-1.128-4.364-2.728 4.365-4.924v-4.275c-.424 0-.847.169-1.147.507z"
      fill="#6df"
    />
    <path
      d="m4.636 10.199.052.032 4.312 2.696h.001v-7.651l-.001-.001z"
      fill="#cbf8ff"
    />
    <path
      d="m17.324 12.078c.751-.469.902-1.457.323-2.111l-4.921-5.551a3.102 3.102 0 0 0 -1.313-.291c-.925 0-1.752.399-2.302 1.026l-.109.123 4.364 4.924-4.365 2.728v4.073c.287 0 .573-.078.823-.234l7.5-4.688z"
      fill="#074793"
    />
    <path
      d="m9.001 1v4.275l.109-.123a3.053 3.053 0 0 1 2.302-1.026c.472 0 .916.107 1.313.291l-2.579-2.909a1.524 1.524 0 0 0 -1.146-.507z"
      fill="#0294e4"
    />
    <path d="m13.365 10.199-4.364-4.923v7.65z" fill="#96bcc2" />
  </svg>
);

const GoogleIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

interface GoogleAuthorizationCodeFlowProps {
  email: string;
  hostedDomain?: string;
  onSuccess: (codeResponse: CodeResponse) => void;
  onError: (errorResponse: unknown) => void;
}

const LOGIN_REDIRECT_PREFIX = "/login/r/";

export const getPostLoginRedirectPath = (pathname: string) =>
  pathname.startsWith(LOGIN_REDIRECT_PREFIX)
    ? `/${pathname.slice(LOGIN_REDIRECT_PREFIX.length)}`
    : "/";

const GoogleAuthorizationCodeFlow: React.FC<
  GoogleAuthorizationCodeFlowProps
> = ({ email, hostedDomain, onSuccess, onError }) => {
  const requestAuthorizationCode = useGoogle({
    scope: GEARS_CONSTANTS.GOOGLE_DIRECTORY_SCOPES,
    flow: "auth-code",
    hint: email,
    hosted_domain: hostedDomain,
    select_account: false,
    onSuccess,
    onError,
  });

  useEffect(() => {
    requestAuthorizationCode();
  }, [requestAuthorizationCode]);

  return null;
};

export const Login: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [disabled, setDisabled] = useState(false);
  const user = useSelector((state: State) => state.user);
  const domain = process.env.REACT_APP_GOOGLE_AUTH_HOSTED_DOMAIN;
  const googleLoginInProgress = useRef(false);
  const googleGatewayBootstrap = useRef<Promise<unknown> | null>(null);
  const googleAuthorizationAttempt = useRef(0);
  const [googleAuthorizationRequest, setGoogleAuthorizationRequest] = useState<{
    email: string;
    attempt: number;
  } | null>(null);
  const isGoogleLoginPage = location.pathname === "/login/google";
  const clearGoogleBootstrap = () => {
    googleLoginInProgress.current = false;
    googleGatewayBootstrap.current = null;
    setGoogleAuthorizationRequest(null);
    sessionStorage.removeItem(ID_TOKEN);
    sessionStorage.removeItem(GEARS_TOKEN);
    sessionStorage.removeItem(GEARS_TOKEN_EXPIRATION_DATE);
  };

  const onEntraSignIn = async () => {
    try {
      setDisabled(true);
      const msalInstance = getMsalInstance();
      await msalInstance.initialize();
      const result = await msalInstance.loginPopup({
        scopes: ENTRA_LOGIN_SCOPES,
      });
      const entraUserDetails = parseJwt(result.idToken);
      // upn is an optional claim and may be absent; preferred_username is the standard v2.0 equivalent (matches backend's EntraAuthService fallback)
      const upn = entraUserDetails?.upn;
      const data = {
        id: entraUserDetails?.sub,
        name: entraUserDetails?.name,
        email: upn,
        upn: upn,
        avatar: entraUserDetails?.avatar,
        id_token: result.idToken,
        access_token: result.accessToken,
      };

      dispatch(setUser(data && { ...data, provider: "entra" }));
      dispatch(getSession());
    } catch (err) {
      console.log(err);
      if (err?.response) {
        alertService.danger({ statusCode: err.response.status });
      } else if (err?.errorCode !== "user_cancelled") {
        alertService.danger({
          msg: "Global Payments sign-in failed. Please try again.",
        });
      }
    } finally {
      setDisabled(false);
    }
  };

  const onGoogleAuthorizationCodeSuccess = async (
    codeResponse: CodeResponse
  ) => {
    try {
      setDisabled(true);
      const bootstrap = googleGatewayBootstrap.current;
      if (!bootstrap) {
        throw new Error("Google gateway bootstrap did not start.");
      }
      await bootstrap;
      const { data } = await httpClient.post(
        "/api/v1/google/auth",
        codeResponse.code
      );
      dispatch(setUser(data && { ...data, provider: "google" }));
      dispatch(getSession());
    } catch (err) {
      clearGoogleBootstrap();
      console.log(err);
      alertService.danger(
        err?.response
          ? { statusCode: err.response.status }
          : { msg: "TSYS / FIS Issuing sign-in failed. Please try again." }
      );
    } finally {
      googleLoginInProgress.current = false;
      googleGatewayBootstrap.current = null;
      setGoogleAuthorizationRequest(null);
      setDisabled(false);
    }
  };

  const onGoogleAuthorizationCodeError = (errorResponse: unknown) => {
    clearGoogleBootstrap();
    setDisabled(false);
    console.log(errorResponse);
    alertService.danger({
      msg: "TSYS / FIS Issuing sign-in failed. Please try again.",
    });
  };

  const onGoogleIdentitySuccess = async (response: CredentialResponse) => {
    if (googleLoginInProgress.current) {
      return;
    }
    if (!response.credential) {
      setDisabled(false);
      alertService.danger({
        msg: "TSYS / FIS Issuing sign-in failed. Please try again.",
      });
      return;
    }

    try {
      googleLoginInProgress.current = true;
      setDisabled(true);
      sessionStorage.setItem(ID_TOKEN, response.credential);
      const bootstrap = apiGeeAuthentication();
      googleGatewayBootstrap.current = bootstrap;
      const email = parseJwt(response.credential)?.email;
      if (!email) {
        throw new Error("Google credential did not include an email address.");
      }
      setGoogleAuthorizationRequest({
        email,
        attempt: ++googleAuthorizationAttempt.current,
      });
      await bootstrap;
    } catch (err) {
      clearGoogleBootstrap();
      setDisabled(false);
      console.log(err);
      alertService.danger(
        err?.response
          ? { statusCode: err.response.status }
          : { msg: "TSYS / FIS Issuing sign-in failed. Please try again." }
      );
    }
  };

  const onGoogleIdentityError = () => {
    clearGoogleBootstrap();
    setDisabled(false);
    alertService.danger({
      msg: "TSYS / FIS Issuing sign-in failed. Please try again.",
    });
  };

  const redirectAfterSuccess = (
    <Redirect to={{ pathname: getPostLoginRedirectPath(location.pathname) }} />
  );

  return user ? (
    redirectAfterSuccess
  ) : (
    <Box
      flexGrow={1}
      display="flex"
      alignItems="center"
      flexDirection="column"
      justifyContent="center"
    >
      {!isGoogleLoginPage && (
        <>
          <Typography variant="h1" component="p" paragraph>
            <Box
              component="strong"
              display="block"
              fontSize="h1.fontSize"
              fontWeight="fontWeightBold"
              color="primary.dark"
            >
              {"GEARS"}
            </Box>
          </Typography>
          <Typography variant="body1" paragraph color="textSecondary">
            Select an Organization to login
          </Typography>
        </>
      )}
      <Box width="100%" maxWidth={isGoogleLoginPage ? 420 : 360}>
        {googleAuthorizationRequest && (
          <GoogleAuthorizationCodeFlow
            key={googleAuthorizationRequest.attempt}
            email={googleAuthorizationRequest.email}
            hostedDomain={domain}
            onSuccess={onGoogleAuthorizationCodeSuccess}
            onError={onGoogleAuthorizationCodeError}
          />
        )}
        {isGoogleLoginPage ? (
          <Box
            border={1}
            borderColor="grey.300"
            borderRadius={4}
            px={5}
            py={4}
            textAlign="center"
          >
            <Typography variant="h6" component="h1" color="textPrimary">
              Sign in to GEARS
            </Typography>
            <Box mt={1} mb={3}>
              <Typography variant="body2" color="textSecondary">
                Use your Global Payments Google account to continue.
              </Typography>
            </Box>
            <Box
              display="flex"
              justifyContent="center"
              style={{
                opacity: disabled ? 0.6 : 1,
                pointerEvents: disabled ? "none" : "auto",
              }}
            >
              <GoogleLogin
                onSuccess={onGoogleIdentitySuccess}
                onError={onGoogleIdentityError}
                auto_select={false}
                hosted_domain={domain}
                text="continue_with"
                width="320"
              />
            </Box>
          </Box>
        ) : (
          <List component="nav">
            <Tooltip
              title="Login with M365 account (e.g. AB123456@globalpayments.com)"
              placement="top"
              arrow
            >
              <ListItem
                button
                divider
                disabled={disabled}
                onClick={onEntraSignIn}
              >
                <ListItemIcon>
                  <Box
                    width={24}
                    height={24}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <GlobalPaymentsIcon />
                  </Box>
                </ListItemIcon>
                <ListItemText primary="Global Payments" />
                <ChevronRightIcon />
              </ListItem>
            </Tooltip>
            <Tooltip
              title="Login with Legacy accounts with Google (e.g. @tsys.com, @globalpay.com, etc.)"
              placement="top"
              arrow
            >
              <ListItem
                button
                divider
                disabled={disabled}
                onClick={() => history.push("/login/google")}
              >
                <ListItemIcon>
                  <Box
                    width={24}
                    height={24}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <GoogleIcon />
                  </Box>
                </ListItemIcon>
                <ListItemText primary="TSYS / FIS Issuing" />
                <ChevronRightIcon />
              </ListItem>
            </Tooltip>
          </List>
        )}
      </Box>
    </Box>
  );
};
